import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Building2, Calendar, DollarSign, TrendingUp, User, MoreVertical } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Opportunity } from '@/types'
import { formatCurrency, formatDate } from '@/lib/utils'
import { GRACKLE_COLORS, CATEGORICAL, SEMANTIC } from '@/lib/theme'

interface MobileOpportunityCardProps {
  opportunity: Opportunity
  isSelected: boolean
  onToggleSelect: (id: string) => void
  onEdit?: (opportunity: Opportunity) => void
  onDelete?: (opportunity: Opportunity) => void
}

export function MobileOpportunityCard({
  opportunity,
  isSelected,
  onToggleSelect,
  onEdit,
  onDelete,
}: MobileOpportunityCardProps) {
  const getStageStyle = (stage?: string) => {
    const stageName = stage?.toLowerCase()
    if (stageName?.includes('closed won')) {
      return { backgroundColor: SEMANTIC.success.fill, color: SEMANTIC.success.text }
    }
    if (stageName?.includes('closed lost')) {
      return { backgroundColor: SEMANTIC.error.fill, color: SEMANTIC.error.text }
    }
    if (stageName?.includes('proposal') || stageName?.includes('negotiation')) {
      return { backgroundColor: CATEGORICAL.steel.tint, color: GRACKLE_COLORS.prussianBlue }
    }
    if (stageName?.includes('qualification')) {
      return { backgroundColor: CATEGORICAL.navy.tint, color: GRACKLE_COLORS.prussianBlue }
    }
    // Default open stage
    return { backgroundColor: CATEGORICAL.teal.tint, color: GRACKLE_COLORS.prussianBlue }
  }

  const getProbabilityStyle = (probability?: number) => {
    if (!probability) return { color: undefined } // Use default muted text
    if (probability >= 70) {
      // High probability - Navy (positive but not green)
      return { color: GRACKLE_COLORS.navy }
    }
    if (probability >= 40) {
      // Medium probability - Prussian (neutral/caution)
      return { color: GRACKLE_COLORS.prussianBlue }
    }
    // Low probability - Dark Raspberry (warning)
    return { color: GRACKLE_COLORS.darkRaspberry }
  }

  return (
    <Card className="relative">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(opportunity.id)}
              aria-label={`Select ${opportunity.name}`}
              className="mt-0.5"
            />
            <div className="flex-1 min-w-0">
              <Link
                to={`/opportunities/${opportunity.id}`}
                className="font-semibold text-base hover:underline focus:underline focus:outline-none"
              >
                {opportunity.name}
              </Link>
              {opportunity.stage_name && (
                <Badge style={getStageStyle(opportunity.stage_name)} className="mt-1 text-xs">
                  {opportunity.stage_name}
                </Badge>
              )}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={`Actions for ${opportunity.name}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/opportunities/${opportunity.id}`}>View Details</Link>
              </DropdownMenuItem>
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(opportunity)}>
                  Edit Opportunity
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(opportunity)}
                  className="text-destructive focus:text-destructive"
                >
                  Delete Opportunity
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {opportunity.account_name && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Building2 className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{opportunity.account_name}</span>
          </div>
        )}

        {opportunity.amount && (
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span className="font-medium">
              {formatCurrency(opportunity.amount, opportunity.currency_iso_code || 'CAD')}
            </span>
          </div>
        )}

        {opportunity.probability !== undefined && opportunity.probability !== null && (
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span style={getProbabilityStyle(opportunity.probability)}>
              {opportunity.probability}% probability
            </span>
          </div>
        )}

        {opportunity.close_date && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span>Close: {formatDate(opportunity.close_date)}</span>
          </div>
        )}

        {opportunity.owner_name && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <User className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{opportunity.owner_name}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

interface MobileOpportunityListProps {
  opportunities: Opportunity[]
  selectedIds: Set<string>
  onToggleSelect: (id: string) => void
  onEdit?: (opportunity: Opportunity) => void
  onDelete?: (opportunity: Opportunity) => void
}

export function MobileOpportunityList({
  opportunities,
  selectedIds,
  onToggleSelect,
  onEdit,
  onDelete,
}: MobileOpportunityListProps) {
  return (
    <div className="space-y-3">
      {opportunities.map((opportunity) => (
        <MobileOpportunityCard
          key={opportunity.id}
          opportunity={opportunity}
          isSelected={selectedIds.has(opportunity.id)}
          onToggleSelect={onToggleSelect}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}
